import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { YourGiftCardPreview } from '../components/YourGiftCardPreview';
import { GiftCardStepper } from '../components/GiftCardStepper';
import {
  getGiftCardDraft,
  patchGiftCardDraft,
} from '../../lib/gift-card-draft';
import { useCustomerAuth } from '../../lib/use-customer-auth';

const occasions = [
  'Birthday',
  'Anniversary',
  'Wedding',
  'Thank You',
  'Congratulations',
  'Just Because',
];

type DeliveryMethod = 'recipient' | 'self';

export default function GiftCardDeliveryPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  // Read every field from the persistent draft — earlier steps wrote
  // amount, sendDate and the recipient details into it.
  const draft = getGiftCardDraft();
  const amount = draft?.amount ?? 50;
  const recipientName = [draft?.firstName, draft?.lastName]
    .filter(Boolean)
    .join(' ');

  const [method, setMethod] = useState<DeliveryMethod>(
    draft?.method === 'SELF' && isAuthenticated ? 'self' : 'recipient',
  );
  const [occasion, setOccasion] = useState<string>(draft?.occasion ?? '');

  const handleContinue = () => {
    patchGiftCardDraft({
      amount: draft?.amount,
      customAmount: draft?.customAmount,
      sendDate: draft?.sendDate,
      firstName: draft?.firstName,
      lastName: draft?.lastName,
      email: draft?.email,
      message: draft?.message,
      method: method === 'self' ? 'SELF' : 'RECIPIENT',
      occasion,
    });
    navigate('/gift-card/details');
  };

  const currentStep = 2;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 w-full">
        <GiftCardStepper currentStep={currentStep} />

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left column */}
          <div className="flex-1 max-w-[740px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-2">
              How should we send it?
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px] mb-6">
              Choose how and when the gift card is delivered.
            </p>

            <DeliveryOption
              selected={method === 'recipient'}
              onClick={() => setMethod('recipient')}
              title="Send Directly to them"
              description="We'll send the gift card straight to your recipient with your personal message included"
            />
            {isAuthenticated && (
              <>
                <div className="h-3" />
                <DeliveryOption
                  selected={method === 'self'}
                  onClick={() => setMethod('self')}
                  title="Send it to me first"
                  description="The gift card comes to your inbox -  perfect if you'd like to present it yourself in person or on card"
                />
              </>
            )}

            <div className="h-px bg-[#ede8e9] w-full my-10" />

            <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-2">
              Anything else?
            </h2>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px] mb-6">
              A few finishing touches for their email.
            </p>

            {/* Gift Occasion select */}
            <div className="relative mb-2">
              <label
                htmlFor="occasion"
                className="absolute left-[20px] top-[10px] font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] pointer-events-none"
              >
                Gift Occasion (Optional)
              </label>
              <select
                id="occasion"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="w-full h-[64px] pt-[26px] pb-[10px] pl-[20px] pr-[44px] bg-white border border-[#eee] rounded-[8px] outline-none appearance-none font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] tracking-[-0.1px] focus:border-[#8e2a2f] transition-colors cursor-pointer"
              >
                <option value="">Select</option>
                {occasions.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <span className="material-icons absolute right-[20px] top-1/2 -translate-y-1/2 text-[#3a3335] text-[18px] pointer-events-none">
                expand_more
              </span>
            </div>
            <p className="font-['Inter',sans-serif] text-[#3a3335] text-[12px] mb-10">
              This will appear on the gift card email they recieve.
            </p>

            {/* Buttons */}
            <div className="hidden md:flex items-center gap-3.5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-[54px] w-[110px] rounded-[10px] bg-[#3a33350a] hover:bg-[#3a333514] flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
              >
                <span className="material-icons text-[18px] text-[#908e8e]">arrow_back</span>
                <span className="font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#908e8e] tracking-[-0.1px]">
                  Back
                </span>
              </button>
              <PrimaryButton onClick={handleContinue} className="w-full max-w-[420px] h-[54px]">
                Continue
              </PrimaryButton>
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:hidden z-50">
              <PrimaryButton onClick={handleContinue} className="w-full h-[54px]">
                Continue
              </PrimaryButton>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full lg:w-[361px] shrink-0">
            <YourGiftCardPreview amount={amount} recipientName={recipientName} />

            <div className="mt-6 border border-[#ede8e9] rounded-[10px] p-5 bg-white">
              <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] tracking-[-0.1px] mb-4">
                Price breakdown
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Gift Card Value
                </span>
                <span className="font-['Inter',sans-serif] text-[#8e2a2f] text-[16px] leading-[24px]">
                  £{amount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="font-['Inter',sans-serif] text-[#908e8e] text-[16px] leading-[24px]">
                  Delivery
                </span>
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Free . Instant Email
                </span>
              </div>
              <div className="h-px bg-[#ede8e9] w-full mb-4" />
              <div className="flex justify-between items-center">
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px]">
                  Total
                </span>
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px]">
                  £{amount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="material-icons text-[#8e2a2f] text-[20px] leading-[21px]">
                  redeem
                </span>
                <p className="font-['DM_Sans',sans-serif] font-medium text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                  Redeemable against any Cherry Picked service
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

interface DeliveryOptionProps {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
}

function DeliveryOption({ selected, onClick, title, description }: DeliveryOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-[8px] border bg-white p-4 transition-colors cursor-pointer ${
        selected ? 'border-[#8e2a2f]' : 'border-[#eee] hover:border-[#8e2a2f]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
            selected ? 'bg-[#8e2a2f]' : 'border-2 border-[#dde7e4]'
          }`}
        >
          <div
            className={`rounded-full w-[7px] h-[7px] ${selected ? 'bg-white' : 'bg-[#c6d8d3]'}`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[21px] tracking-[-0.1px]">
            {title}
          </p>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] leading-[18px]">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
}
